import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { Amplify } from "aws-amplify";
import { events } from "aws-amplify/data";
import { useAuth0 } from "@auth0/auth0-react";
import { ConfigurationContext } from "@utils/ConfigurationProvider";

export const RealtimeContext = createContext(null);

// Backoff for rebuilding a dropped socket (network blip, idle disconnect, or an auth
// failure that outlives Amplify's own internal retry).
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
// Re-establish the socket this long before the auth token expires. AppSync holds the
// token as a static string for the life of the connection (aws-amplify can't refresh it
// in place), so on a long session the socket silently dies when the token lapses unless
// we rebuild it ahead of time.
const TOKEN_REFRESH_LEAD_MS = 60000;
// Fallback token lifetime when Auth0 doesn't report one, so we never busy-reconnect.
const DEFAULT_TOKEN_TTL_MS = 50 * 60 * 1000;

export function RealtimeProvider({ children }) {
  const { config } = useContext(ConfigurationContext);
  const { getAccessTokenSilently } = useAuth0();

  const endpoint = config?.appSyncHttpEndpoint;
  const region = config?.appSyncRegion;
  const audience = config?.authAudience;

  const registry = useRef(new Map());
  const configured = useRef(false);

  useEffect(() => {
    if (!endpoint || !region || configured.current) return;
    Amplify.configure({ API: { Events: { endpoint, region, defaultAuthMode: "lambda" } } });
    configured.current = true;
  }, [endpoint, region]);

  const subscribe = useCallback(
    (channelPath, handler) => {
      if (!endpoint || !region || !channelPath) return () => {};

      const reg = registry.current;
      let entry = reg.get(channelPath);
      const isNewEntry = !entry;

      if (isNewEntry) {
        entry = {
          handlers: new Set(),
          refs: 0,
          channel: null,
          sub: null,
          closed: false,
          refreshTimer: null,
          reconnectTimer: null,
          reconnectAttempt: 0,
        };
        reg.set(channelPath, entry);
      }

      // Register this consumer before kicking off the initial connect, so the connect's
      // "all consumers left" guard sees the live refcount rather than a transient zero.
      entry.handlers.add(handler);
      entry.refs += 1;

      if (isNewEntry) {
        // Drop the live channel/subscription and its refresh timer without touching the
        // handlers or refcount, so a reconnect rebuilds on top of the same consumers.
        function teardownConnection() {
          clearTimeout(entry.refreshTimer);
          entry.refreshTimer = null;
          try {
            entry.sub?.unsubscribe?.();
            entry.channel?.close?.();
          } catch {
            /* no-op */
          }
          entry.sub = null;
          entry.channel = null;
        }

        function scheduleReconnect() {
          if (entry.closed || entry.refs === 0 || entry.reconnectTimer) return;
          const delay =
            RECONNECT_DELAYS_MS[Math.min(entry.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
          entry.reconnectAttempt += 1;
          entry.reconnectTimer = setTimeout(() => {
            entry.reconnectTimer = null;
            connect();
          }, delay);
        }

        async function connect() {
          if (entry.closed || entry.refs === 0) return;
          teardownConnection();
          try {
            const { access_token: authToken, expires_in: expiresIn } = await getAccessTokenSilently(
              { authorizationParams: { audience, scope: "access" }, detailedResponse: true },
            );
            if (entry.closed || entry.refs === 0) return; // all consumers left before connect resolved
            const channel = await events.connect(channelPath, { authMode: "lambda", authToken });
            if (entry.closed || entry.refs === 0) {
              channel?.close?.();
              return;
            }
            entry.channel = channel;
            entry.reconnectAttempt = 0;
            entry.sub = channel.subscribe({
              next: (msg) => entry.handlers.forEach((h) => h(msg)),
              error: (err) => {
                console.warn("realtime subscribe error", channelPath, err);
                scheduleReconnect();
              },
            });
            // Rebuild with a fresh token just before this one expires so a long-lived
            // session never ends up on a dead, un-refreshable socket.
            const ttlMs = expiresIn ? expiresIn * 1000 : DEFAULT_TOKEN_TTL_MS;
            const lead = Math.max(ttlMs - TOKEN_REFRESH_LEAD_MS, TOKEN_REFRESH_LEAD_MS);
            entry.refreshTimer = setTimeout(connect, lead);
          } catch (err) {
            console.warn("realtime connect failed", channelPath, err);
            scheduleReconnect();
          }
        }

        connect();
      }

      return () => {
        entry.handlers.delete(handler);
        entry.refs -= 1;
        if (entry.refs <= 0) {
          entry.closed = true;
          clearTimeout(entry.refreshTimer);
          clearTimeout(entry.reconnectTimer);
          try {
            entry.sub?.unsubscribe?.();
            entry.channel?.close?.();
          } catch {
            /* no-op */
          }
          reg.delete(channelPath);
        }
      };
    },
    [endpoint, region, audience, getAccessTokenSilently],
  );

  return <RealtimeContext.Provider value={subscribe}>{children}</RealtimeContext.Provider>;
}
